import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import {
    useFetchQrByBatchRunningCodeMutation,
    useCheckQrScannedStatusMutation,
} from "../../../features/tenant/qrCode/qrCodeApi";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useNavigate } from "react-router-dom";
import ViewQr from "../../../components/Tenant/ViewQR/ViewQr";
import { getVendorName } from "../../../utils/getVendorName";

const SearchByBatchRunningCode = () => {
    const navigate = useNavigate();

    // Input state
    const [searchCode, setSearchCode] = useState("");
    const [searchType, setSearchType] = useState("auto"); // auto, unique_code, scratch_code, batch_running_code, qr_id
    const [activeTab, setActiveTab] = useState("overview"); // overview, qr_details, scan_details, scheme_details

    // Scan Status Check State
    const [statusResult, setStatusResult] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError, setStatusError] = useState(null);

    // RTK Query Mutations
    const [checkQrScannedStatusFun] = useCheckQrScannedStatusMutation();
    const [qrListDataFun, { data: qrListData, isLoading: qrListDataIsLoading }] =
        useFetchQrByBatchRunningCodeMutation();

    // Batch List Table State
    const [request, setRequest] = useState({
        batch_running_code: "",
        qr_barcode: "",
    });
    const [tableColumns, setTableColumns] = useState([]);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationResetToggle, setPaginationResetToggle] = useState(false);
    const [showQr, setShowQr] = useState(false);
    const [qrData, setQrData] = useState(null);

    // Trigger QR Scan Status Check
    const handleCheckStatus = async (e) => {
        if (e) e.preventDefault();
        if (!searchCode.trim()) {
            setStatusError("Please enter a QR code, scratch code, batch code, or QR ID");
            return;
        }

        setStatusLoading(true);
        setStatusError(null);
        setStatusResult(null);

        try {
            const token = sessionStorage.getItem("access_token");
            const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
            const tenant_id = vendorData.tenant_id;

            const payload = {};
            const codeTrimmed = searchCode.trim();

            if (searchType === "qr_id" || (!isNaN(codeTrimmed) && searchType === "auto")) {
                payload.qr_id = codeTrimmed;
            } else if (searchType === "scratch_code") {
                payload.scratch_code = codeTrimmed;
            } else if (searchType === "batch_running_code") {
                payload.batch_running_code = codeTrimmed;
            } else if (searchType === "unique_code") {
                payload.unique_code = codeTrimmed;
            } else {
                // Auto detect
                payload.unique_code = codeTrimmed;
                payload.code = codeTrimmed;
            }

            const response = await checkQrScannedStatusFun({
                token,
                tenant_id,
                body: payload,
            }).unwrap();

            if (response && response.body) {
                setStatusResult(response.body);
            } else if (response && response.message) {
                setStatusError(response.message);
            } else {
                setStatusError("Unable to fetch QR status details.");
            }
        } catch (err) {
            console.error("Error checking QR status:", err);
            const errMsg = err?.data?.message || err?.message || "Failed to fetch QR status. Please try again.";
            setStatusError(errMsg);
        } finally {
            setStatusLoading(false);
        }
    };

    const handleClearSearch = () => {
        setSearchCode("");
        setSearchType("auto");
        setStatusResult(null);
        setStatusError(null);
    };

    // Columns for legacy batch table view
    const columns = [
        {
            name: "Batch Running Code",
            cell: (row) => row.batch_running_code,
            sortable: true,
            width: "165px",
        },
        {
            name: "Product Code",
            cell: (row) => row.product_code,
            sortable: true,
            width: "140px",
        },
        {
            name: "Qr Code",
            cell: (row) => row.unique_code,
            sortable: true,
            width: "130px",
        },
        {
            name: "Scratch Code",
            cell: (row) => row.scratch_code,
            sortable: true,
        },
        {
            name: "Type",
            cell: (row) => (
                <span
                    className={`text-sm p-1 capitalize rounded font-semibold text-white ${
                        row.parent_code === null
                            ? "bg-gray-500"
                            : row.parent_code === "false"
                            ? "bg-red-500"
                            : "bg-blue-500"
                    }`}
                >
                    {row.parent_code === null
                        ? "Child"
                        : row.parent_code === "false"
                        ? "Parent"
                        : "Child"}
                </span>
            ),
            sortable: true,
            width: "100px",
        },
        {
            name: "MRP",
            cell: (row) => row.mrp,
            sortable: true,
            width: "100px",
        },
        {
            name: "Qr Status",
            cell: (row) => (
                <span
                    className={`text-sm px-2 py-0.5 capitalize rounded font-bold text-white ${
                        row.qr_status == "2" ? "bg-red-600" : "bg-emerald-600"
                    }`}
                >
                    {row.qr_status == "2" ? "Scanned" : "Not Scanned"}
                </span>
            ),
            sortable: true,
            width: "130px",
        },
        {
            name: "Created At",
            cell: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : "-",
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="flex gap-1.5 flex-wrap">
                    <button
                        className="rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 transition"
                        onClick={() =>
                            navigate(`/genefied/${getVendorName()}/tracking`, {
                                state: row,
                            })
                        }
                    >
                        Track
                    </button>
                    <button
                        className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 transition"
                        onClick={() => handleShowQr(row)}
                    >
                        GVerify Qr
                    </button>
                    <button
                        className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700 transition"
                        onClick={() => handleWebGenefiedQr(row)}
                    >
                        Web Qr
                    </button>
                    <button
                        className="rounded bg-teal-600 px-2 py-1 text-xs font-medium text-white hover:bg-teal-700 transition"
                        onClick={() => handleBatchShowQr(row)}
                    >
                        View Qr
                    </button>
                </div>
            ),
            width: "320px",
        },
    ];

    const handleShowQr = (data) => {
        let obj = {
            link: data.unique_code?.includes("gverify.me")
                ? data.unique_code
                : `http://gverify.me/?${data.unique_code}`,
            ...data,
        };
        setQrData(obj);
        setShowQr(true);
    };

    const handleBatchShowQr = (data) => {
        let obj = {
            link: `${data.unique_code}`,
            ...data,
        };
        const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
        if (vendorData.tenant_id == 17) {
            obj.link = `${process.env.REACT_APP_QR_URL}genefied/verifyQr?qrCode=${data.unique_code}`;
        }
        setQrData(obj);
        setShowQr(true);
    };

    const handleWebGenefiedQr = (data) => {
        let obj = {
            link: `${process.env.REACT_APP_QR_URL}genefied/verifyQr?qrCode=${data.unique_code}`,
            ...data,
        };
        setQrData(obj);
        setShowQr(true);
    };

    const fetchAllQrCodeFunction = () => {
        const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
        qrListDataFun({
            token: sessionStorage.getItem("access_token"),
            tenant_id: vendorData.tenant_id,
            body: request,
        });
    };

    useEffect(() => {
        const c = [];
        const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
        if (vendorData.tenant_name === "jqr") {
            c.push({
                name: "Invoice Number",
                cell: (row) => row?.details?.invoice_no,
                sortable: true,
                width: "180px",
            });
            c.push({
                name: "Distributor Code",
                cell: (row) => row?.details?.user_id,
                sortable: true,
                width: "150px",
            });
        } else {
            c.push({
                name: "Mapped Mobile",
                cell: (row) => row?.mobile || "-",
                sortable: true,
                width: "140px",
            });
            c.push({
                name: "Mapped User Name",
                cell: (row) => row?.name || "-",
                sortable: true,
                width: "140px",
            });
        }
        setTableColumns([...c, ...columns]);
    }, []);

    const handlePageChange = (pageNumber) => {
        const newOffset = (pageNumber - 1) * limit;
        setOffset(newOffset);
        setRequest((prevRequest) => ({ ...prevRequest, offset: newOffset }));
        setCurrentPage(pageNumber);
    };

    const handlePerRowsChange = (newPerPage) => {
        setRequest((prevRequest) => ({
            ...prevRequest,
            limit: newPerPage,
            offset: 0,
        }));
        setLimit(newPerPage);
        setOffset(0);
        setCurrentPage(1);
        setPaginationResetToggle((prev) => !prev);
    };

    return (
        <div className="flex w-full bg-gray-100 min-h-screen flex-col">
            {/* Header section */}
            <div className="border-b bg-white border-gray-200 px-6 py-4 shadow-sm">
                <div className="flex justify-between items-center flex-wrap gap-2">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">
                            QR Code Scan Status & Verification Inspector
                        </h1>
                        <p className="text-xs text-gray-500 mt-1">
                            Check if a QR code is valid, scanned or not scanned, inspect wallet pipeline (Main vs Scheme Wallet), and view detailed scan entry logs.
                        </p>
                    </div>
                    <LinksComponent />
                </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto w-full">
                {/* Search Box Card */}
                <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 px-6 py-4 text-white">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Check QR Code Status
                        </h2>
                        <p className="text-xs text-blue-100 mt-0.5">
                            Enter Unique Code, Scratch Code, Batch Running Code, or QR ID
                        </p>
                    </div>

                    <form onSubmit={handleCheckStatus} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                                    QR Identifier / Code
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        className="w-full pl-10 pr-4 py-2.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                        placeholder="e.g. Unique Code, Scratch Code, Batch Running Code, or QR ID..."
                                        value={searchCode}
                                        onChange={(e) => setSearchCode(e.target.value)}
                                        required
                                    />
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                                    Code Type
                                </label>
                                <select
                                    className="w-full px-3 py-2.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                    value={searchType}
                                    onChange={(e) => setSearchType(e.target.value)}
                                >
                                    <option value="auto">Auto Detect</option>
                                    <option value="unique_code">Unique Code (QR Code)</option>
                                    <option value="scratch_code">Scratch Code</option>
                                    <option value="batch_running_code">Batch Running Code</option>
                                    <option value="qr_id">QR ID (Numeric)</option>
                                </select>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    disabled={statusLoading}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium py-2.5 px-4 rounded-lg shadow transition flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {statusLoading ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Checking...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Check Status
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClearSearch}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2.5 px-3 rounded-lg transition text-xs"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Error Banner */}
                {statusError && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg shadow-sm">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-red-800">{statusError}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Status Result Details Box */}
                {statusResult && (
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden space-y-6 p-6">
                        {/* Summary Badges Header */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                            {/* Validity Badge */}
                            <div className="flex items-center gap-3 bg-white p-3.5 rounded-lg border border-gray-200 shadow-sm">
                                <div className={`p-2.5 rounded-full ${statusResult.is_valid ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>
                                    {statusResult.is_valid ? (
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-semibold uppercase">QR Validity</p>
                                    <p className={`text-base font-bold ${statusResult.is_valid ? "text-emerald-700" : "text-red-700"}`}>
                                        {statusResult.is_valid ? "Valid QR Code" : "Invalid / Deactivated QR"}
                                    </p>
                                </div>
                            </div>

                            {/* Scan Status Badge */}
                            <div className="flex items-center gap-3 bg-white p-3.5 rounded-lg border border-gray-200 shadow-sm">
                                <div className={`p-2.5 rounded-full ${statusResult.is_scanned ? "bg-purple-100 text-purple-600" : "bg-blue-100 text-blue-600"}`}>
                                    {statusResult.is_scanned ? (
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-semibold uppercase">Scan Status</p>
                                    <p className={`text-base font-bold ${statusResult.is_scanned ? "text-purple-700" : "text-blue-700"}`}>
                                        {statusResult.is_scanned ? "SCANNED" : "NOT SCANNED"}
                                    </p>
                                </div>
                            </div>

                            {/* Wallet Pipeline Badge */}
                            <div className="flex items-center gap-3 bg-white p-3.5 rounded-lg border border-gray-200 shadow-sm">
                                <div className={`p-2.5 rounded-full ${
                                    statusResult.wallet_type === "main_wallet"
                                        ? "bg-indigo-100 text-indigo-600"
                                        : statusResult.wallet_type === "scheme_wallet"
                                        ? "bg-amber-100 text-amber-600"
                                        : "bg-gray-100 text-gray-500"
                                }`}>
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-semibold uppercase">Wallet Pipeline</p>
                                    <p className="text-base font-bold text-gray-900">
                                        {statusResult.wallet_name || (statusResult.is_scanned ? "Active" : "None")}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Navigation Tabs */}
                        <div className="border-b border-gray-200">
                            <nav className="flex space-x-6">
                                <button
                                    onClick={() => setActiveTab("overview")}
                                    className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
                                        activeTab === "overview"
                                            ? "border-blue-600 text-blue-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    Overview
                                </button>
                                <button
                                    onClick={() => setActiveTab("qr_details")}
                                    className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
                                        activeTab === "qr_details"
                                            ? "border-blue-600 text-blue-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    QR Code Details
                                </button>
                                {statusResult.entry_details && (
                                    <button
                                        onClick={() => setActiveTab("scan_details")}
                                        className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
                                            activeTab === "scan_details"
                                                ? "border-blue-600 text-blue-600"
                                                : "border-transparent text-gray-500 hover:text-gray-700"
                                        }`}
                                    >
                                        Point Entry / Scan Log
                                    </button>
                                )}
                                {statusResult.scheme_details && (
                                    <button
                                        onClick={() => setActiveTab("scheme_details")}
                                        className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
                                            activeTab === "scheme_details"
                                                ? "border-blue-600 text-blue-600"
                                                : "border-transparent text-gray-500 hover:text-gray-700"
                                        }`}
                                    >
                                        Scheme Details
                                    </button>
                                )}
                            </nav>
                        </div>

                        {/* Tab Contents */}
                        {activeTab === "overview" && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Key Information Summary */}
                                <div className="border border-gray-200 rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white space-y-4">
                                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide border-b pb-2">
                                        QR Summary
                                    </h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-xs text-gray-500 block">QR ID</span>
                                            <span className="font-semibold text-gray-900">
                                                {statusResult.qr_details?.id || statusResult.entry_details?.qr_id || "-"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500 block">Product Code</span>
                                            <span className="font-semibold text-gray-900">
                                                {statusResult.qr_details?.product_code || statusResult.entry_details?.product_code || "-"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500 block">Batch Code</span>
                                            <span className="font-semibold text-gray-900">
                                                {statusResult.qr_details?.batch_code || "-"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500 block">Batch Running Code</span>
                                            <span className="font-semibold text-gray-900">
                                                {statusResult.qr_details?.batch_running_code || "-"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500 block">MRP</span>
                                            <span className="font-semibold text-gray-900">
                                                ₹{statusResult.qr_details?.mrp || statusResult.entry_details?.mrp || "0.00"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500 block">QR Type</span>
                                            <span className="font-semibold text-gray-900">
                                                {statusResult.qr_details?.qr_type === "1" ? "Standard" : statusResult.qr_details?.qr_type || "-"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Scan Entry Summary */}
                                <div className="border border-gray-200 rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white space-y-4">
                                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide border-b pb-2">
                                        Scanning & Wallet Summary
                                    </h3>
                                    {statusResult.is_scanned && statusResult.entry_details ? (
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="text-xs text-gray-500 block">App User ID</span>
                                                <span className="font-semibold text-gray-900">
                                                    {statusResult.entry_details.app_user_id || "-"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500 block">User Type</span>
                                                <span className="font-semibold text-gray-900 capitalize">
                                                    {statusResult.entry_details.user_type || "-"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500 block">Points Earned</span>
                                                <span className="font-bold text-emerald-600">
                                                    {statusResult.entry_details.points || "0"} pts
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500 block">Platform</span>
                                                <span className="font-semibold text-gray-900 capitalize">
                                                    {statusResult.entry_details.platform || "-"}
                                                </span>
                                            </div>
                                            <div className="col-span-2">
                                                <span className="text-xs text-gray-500 block">Scanned At</span>
                                                <span className="font-semibold text-gray-900">
                                                    {statusResult.entry_details.created_at
                                                        ? new Date(statusResult.entry_details.created_at).toLocaleString()
                                                        : "-"}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-6 text-gray-500">
                                            <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <p className="text-sm font-medium">QR code has not been scanned yet.</p>
                                            <p className="text-xs text-gray-400 mt-1">No point entries or scan logs recorded.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === "qr_details" && statusResult.qr_details && (
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50 w-1/3">QR ID</td>
                                            <td className="px-6 py-3 text-gray-900">{statusResult.qr_details.id}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Unique Code (QR Code)</td>
                                            <td className="px-6 py-3 font-mono font-bold text-blue-600">{statusResult.qr_details.unique_code}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Scratch Code</td>
                                            <td className="px-6 py-3 font-mono text-gray-900">{statusResult.qr_details.scratch_code}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Batch Code</td>
                                            <td className="px-6 py-3 text-gray-900">{statusResult.qr_details.batch_code}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Batch Running Code</td>
                                            <td className="px-6 py-3 font-mono text-gray-900">{statusResult.qr_details.batch_running_code}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Product Code</td>
                                            <td className="px-6 py-3 text-gray-900">{statusResult.qr_details.product_code}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">MRP / Original MRP</td>
                                            <td className="px-6 py-3 text-gray-900">₹{statusResult.qr_details.mrp} (Original: ₹{statusResult.qr_details.original_mrp || statusResult.qr_details.mrp})</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">QR Status Flag</td>
                                            <td className="px-6 py-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${statusResult.qr_details.qr_status == 2 ? "bg-red-600" : "bg-emerald-600"}`}>
                                                    {statusResult.qr_details.qr_status == 2 ? "Scanned (2)" : "Not Scanned (1)"}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Is Printed</td>
                                            <td className="px-6 py-3 text-gray-900">{statusResult.qr_details.is_printed ? "Yes" : "No"}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Created At</td>
                                            <td className="px-6 py-3 text-gray-900">{new Date(statusResult.qr_details.created_at).toLocaleString()}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className="p-4 bg-gray-50 border-t flex gap-2 flex-wrap">
                                    <button
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded transition"
                                        onClick={() => handleShowQr(statusResult.qr_details)}
                                    >
                                        GVerify QR
                                    </button>
                                    <button
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-3 py-1.5 rounded transition"
                                        onClick={() => handleWebGenefiedQr(statusResult.qr_details)}
                                    >
                                        Web QR
                                    </button>
                                    <button
                                        className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium px-3 py-1.5 rounded transition"
                                        onClick={() => handleBatchShowQr(statusResult.qr_details)}
                                    >
                                        View QR Image
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === "scan_details" && statusResult.entry_details && (
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50 w-1/3">Entry ID</td>
                                            <td className="px-6 py-3 text-gray-900">{statusResult.entry_details.id}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">App User ID</td>
                                            <td className="px-6 py-3 text-gray-900 font-semibold">{statusResult.entry_details.app_user_id}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">User Type</td>
                                            <td className="px-6 py-3 text-gray-900 capitalize">{statusResult.entry_details.user_type} (ID: {statusResult.entry_details.user_type_id})</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Points Credited</td>
                                            <td className="px-6 py-3 text-emerald-600 font-bold">{statusResult.entry_details.points} pts</td>
                                        </tr>
                                        {statusResult.entry_details.amount && (
                                            <tr>
                                                <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Amount</td>
                                                <td className="px-6 py-3 text-gray-900">₹{statusResult.entry_details.amount}</td>
                                            </tr>
                                        )}
                                        {statusResult.entry_details.product_name && (
                                            <tr>
                                                <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Product Name</td>
                                                <td className="px-6 py-3 text-gray-900">{statusResult.entry_details.product_name} ({statusResult.entry_details.product_code})</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Platform</td>
                                            <td className="px-6 py-3 text-gray-900 capitalize">{statusResult.entry_details.platform} (ID: {statusResult.entry_details.platform_id})</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Status</td>
                                            <td className="px-6 py-3 text-gray-900">{statusResult.entry_details.status || "1"}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Is Reverted</td>
                                            <td className="px-6 py-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${statusResult.entry_details.is_reverted ? "bg-red-600" : "bg-emerald-600"}`}>
                                                    {statusResult.entry_details.is_reverted ? "Yes (Reverted)" : "No"}
                                                </span>
                                            </td>
                                        </tr>
                                        {statusResult.entry_details.remark && (
                                            <tr>
                                                <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Remark</td>
                                                <td className="px-6 py-3 text-gray-900">{statusResult.entry_details.remark}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Entry Created At</td>
                                            <td className="px-6 py-3 text-gray-900">{new Date(statusResult.entry_details.created_at).toLocaleString()}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {activeTab === "scheme_details" && statusResult.scheme_details && (
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50 w-1/3">Scheme ID</td>
                                            <td className="px-6 py-3 text-gray-900">{statusResult.scheme_details.id}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Scheme Name</td>
                                            <td className="px-6 py-3 font-bold text-amber-700">{statusResult.scheme_details.name}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Scheme Type</td>
                                            <td className="px-6 py-3 text-gray-900 capitalize">{statusResult.scheme_details.type}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Scheme Start & End</td>
                                            <td className="px-6 py-3 text-gray-900">
                                                {new Date(statusResult.scheme_details.scheme_start).toLocaleDateString()} to {new Date(statusResult.scheme_details.scheme_end).toLocaleDateString()}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Redeem Start & End</td>
                                            <td className="px-6 py-3 text-gray-900">
                                                {new Date(statusResult.scheme_details.redeem_start).toLocaleDateString()} to {new Date(statusResult.scheme_details.redeem_end).toLocaleDateString()}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-3 font-semibold text-gray-600 bg-gray-50">Status</td>
                                            <td className="px-6 py-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${statusResult.scheme_details.status == "1" ? "bg-emerald-600" : "bg-gray-500"}`}>
                                                    {statusResult.scheme_details.status == "1" ? "Active (1)" : statusResult.scheme_details.status}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Legacy / Full Batch QR Code Table Section */}
                {/* <div className="bg-white shadow-md rounded-xl border border-gray-200 overflow-hidden">
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h3 className="text-base font-bold text-gray-800">
                                All QR Codes List (By Batch Running Code)
                            </h3>
                            <p className="text-xs text-gray-500">
                                Filter all QR codes by Batch Running Code or Barcode to view paginated records.
                            </p>
                        </div>
                    </div>

                    <div className="p-4 border-b border-gray-200 bg-gray-50/50">
                        <div className="flex flex-wrap gap-4 items-end justify-between">
                            <div className="flex flex-wrap gap-3 flex-1">
                                <div className="w-full sm:w-64">
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                        Batch Running Code
                                    </label>
                                    <input
                                        type="search"
                                        className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Batch Running Code..."
                                        value={request.batch_running_code}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setRequest((prevState) => ({
                                                ...prevState,
                                                batch_running_code: newValue,
                                            }));
                                        }}
                                    />
                                </div>

                                <div className="w-full sm:w-64">
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                        QR / Barcode
                                    </label>
                                    <input
                                        type="search"
                                        className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="QR / Barcode..."
                                        value={request.qr_barcode}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setRequest((prevState) => ({
                                                ...prevState,
                                                qr_barcode: newValue,
                                            }));
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm transition"
                                    onClick={fetchAllQrCodeFunction}
                                >
                                    Search Batch
                                </button>
                                <button
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium px-3 py-2 rounded-lg text-sm transition"
                                    onClick={() => setRequest({ batch_running_code: "", qr_barcode: "" })}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>

                    <DataTableComponent
                        keyField={(row) => row.id || row.unique_code}
                        columns={tableColumns}
                        data={qrListData && qrListData.body}
                        loading={qrListDataIsLoading}
                        handlePerRowsChange={handlePerRowsChange}
                        handlePageChange={handlePageChange}
                        paginationDefaultPage={currentPage}
                        paginationResetDefaultPage={paginationResetToggle}
                    />
                </div> */}
            </div>

            {/* View QR Modal */}
            {showQr && (
                <ViewQr data={qrData} show={showQr} setShow={setShowQr} />
            )}
        </div>
    );
};

export default SearchByBatchRunningCode;
